/**
 * Internal Autopilot Toggle API Contract
 *
 * POST /internal/platform-data-sync/autopilot   — set a school's Autopilot flag
 * GET  /internal/platform-data-sync/autopilot   — read merged effective state + audit
 *
 * API-key-gated (`INTERNAL_API_KEY`). "Autopilot" is the per-school
 * scheduled-sync lane gate: a `SystemConfiguration` row
 * (`category: 'PLATFORM_SYNC_SETTINGS'`, `key: 'AUTOPILOT'`,
 * `value: { enabled: boolean }`). The staleness scheduler only enqueues
 * scheduled jobs for schools whose merged (system + per-school) Autopilot is
 * `enabled: true`; the MANUAL lane (`run-job`, `execute-pending?source=manual`)
 * does NOT gate on it.
 *
 * This is DISTINCT from `School.platformSyncEnabled` (the master kill-switch) —
 * this contract never touches that.
 *
 * Audit trail: the per-school toggle records `{ reason, changedBy, changedAt }`
 * in the SystemConfiguration `metadata` JSON column (kept separate from the
 * functional `value.enabled` the scheduler reads). The GET surfaces it back.
 */

import { z } from 'zod'

/**
 * Audit record for a single Autopilot toggle, stored in `SystemConfiguration.metadata`.
 */
export const AutopilotAuditSchema = z.object({
  reason: z.string().nullable().describe('Free-text reason supplied by the caller, if any'),
  changedBy: z.string().describe("Caller identity (defaults to 'internal-api')"),
  changedAt: z.string().describe('ISO timestamp of the toggle')
})

export type AutopilotAudit = z.infer<typeof AutopilotAuditSchema>

/**
 * Which configuration level supplied the effective `enabled` value.
 *  - `school`: a per-school AUTOPILOT row exists (overrides system)
 *  - `system`: only the system-wide AUTOPILOT row exists
 *  - `none`:   neither exists → effective `enabled` defaults to false
 */
export const AutopilotSourceLevelSchema = z.enum(['school', 'system', 'none'])

export type AutopilotSourceLevel = z.infer<typeof AutopilotSourceLevelSchema>

/**
 * POST request body. `school_id` is a public id or slug; `enabled` is the new
 * per-school flag; `reason` is an optional audit note.
 */
export const SetAutopilotRequestSchema = z.object({
  school_id: z.string().min(1).describe('School public ID (CUID) or slug'),
  enabled: z.boolean().describe('New per-school Autopilot state'),
  reason: z.string().max(2000).optional().describe('Optional audit reason for the change'),
  changed_by: z
    .string()
    .max(200)
    .optional()
    .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
})

export type SetAutopilotRequest = z.infer<typeof SetAutopilotRequestSchema>

/**
 * The merged effective Autopilot state for a school, plus the per-school audit.
 * `enabled` mirrors the staleness gate's merge precedence (per-school overrides
 * system; neither present → false).
 */
export const AutopilotStateSchema = z.object({
  schoolId: z.string().describe("School's public ID"),
  enabled: z.boolean().describe('Merged effective Autopilot state (per-school over system)'),
  source: AutopilotSourceLevelSchema.describe('Which level supplied the effective value'),
  systemEnabled: z.boolean().nullable().describe('System-wide AUTOPILOT enabled (null if no system row)'),
  schoolEnabled: z.boolean().nullable().describe('Per-school AUTOPILOT enabled (null if no per-school row)'),
  audit: AutopilotAuditSchema.nullable().describe('Per-school audit record (null if never set per-school)')
})

export type AutopilotState = z.infer<typeof AutopilotStateSchema>

/**
 * POST response.
 */
export const SetAutopilotResponseSchema = z.object({
  message: z.string(),
  data: AutopilotStateSchema
})

export type SetAutopilotResponse = z.infer<typeof SetAutopilotResponseSchema>

/**
 * GET request (query). `school_id` is a public id or slug.
 */
export const GetAutopilotRequestSchema = z.object({
  school_id: z.string().min(1).describe('School public ID (CUID) or slug')
})

export type GetAutopilotRequest = z.infer<typeof GetAutopilotRequestSchema>

/**
 * GET response.
 */
export const GetAutopilotResponseSchema = z.object({
  message: z.string(),
  data: AutopilotStateSchema
})

export type GetAutopilotResponse = z.infer<typeof GetAutopilotResponseSchema>
